// © Microsoft Corporation. All rights reserved.

/**
 * Enum to set standardized z-index values.
 *
 * // EXAMPLE:
 * myClass: {
 *      "z-index": ZIndex.Above.toString()
 * }
 *
 * Use these constants to specify z-index values in the root stacking context. If you have created another stacking context,
 * these values will have no meaning.
 *
 * Adequate room is left in each block for additional levels to be added if needed.
 *
 * OTHER GUIDANCE:
 * Easily increment within a block by adding an integer like the below example.
 * Consider a scenario where you need to increase something just above the navigation level:
 *
 * myClass: {
 *      "z-index": `${ZIndex.Nav + 1}`
 * }
 *
 * You may also find it useful to increment by other values in the case where you are within a given stacking context.
 *
 * Recommended reading: https://philipwalton.com/articles/what-no-one-told-you-about-z-index/
 *
 * Note: If you have a reason to use a value above CookieWall, Make sure it is not causing a compliance violation.
 */
export enum ZIndex {
    /** Use for cookie wall (Compliance). */
    CookieWall = 1100,

    /** A modal dialog that needs a button press to be dismissed. */
    Dialog = 900,

    /** Any kind of easily-dismissed flyout */
    Flyout = 700,

    /** An overlay that darkens the page to display a flyout or dialog. */
    Overlay = 600,

    /** Navigation pivots. */
    Nav = 500,

    /** Items that need to appear over the page content, like a fixed header. */
    Over = 300,

    /** Items in the main page content that need to float above the rest, like floating buttons on top of a content card. */
    Above = 100,

    /** Default browser element z-index (baseline). */
    Default = 0,

    /** Use for elements that need to live below the normal flow of the document. */
    Below = -1,

    /** Reserved for lowest elements in the stack. */
    Buried = -2
}
